// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import SectionWrapper, { SectionWrapperStyles } from "@pacific/App/Components/Common/SectionWrapper";
import Notify from "@pacific/App/Components/Notify";
import { xHasRoles } from "@pacific/App/Store/Selectors/SessionSelectors";
import { USER_ROLES } from "@pacific/XDL/constants";
import { xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import * as React from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const requisitionerRole = xHasRoles(state, [USER_ROLES.REQUISITIONER]);
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && !requisitionerRole,
        // XNG-Change
        isAuthenticated,
        currentBillTo: getCurrentBillToState(state).value,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

// XNG-Changes: add numberOfRecords & title & viewLinkText
const enum fields {
    title = "title",
    numberOfRecords = "numberOfRecords",
    viewLinkText = "viewLinkText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.title]: string;
        [fields.viewLinkText]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    tableWrapper?: GridItemProps;
    sectionWrapperStyles?: SectionWrapperStyles;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    sectionWrapperStyles: {
        accrodionTitle: {
            wrapper: {
                css: css`
                    ${xScreen("lte", "sm")} {
                        margin-bottom: 0;
                        padding-left: 16px;
                    }
                `,
            },
        },
    },
    tableWrapper: {
        css: css`
            overflow: auto;
        `,
    },
    noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            recentQuotesParameter.pageSize = this.props.fields.numberOfRecords || recentQuotesParameter.pageSize;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    // XNG-Change: GA event
    _GaRecentQoutesEvent(title?: string, url?: string) {
        const { isAuthenticated, session, currentBillTo } = this.props;
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "recent_quotes",
            click_text: title,
            destination: url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    }


    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes, fields } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        return (
            <SectionWrapper
                isAccordion={true}
                title={fields.title || translate("Recent Quotes")}
                link={myQuotesPageUrl}
                linkText={fields.viewLinkText}
                onLinkClick={() => this._GaRecentQoutesEvent(fields.viewLinkText, myQuotesPageUrl)}
                extendedStyles={styles.sectionWrapperStyles}
            >
                {quotesDataView.value.length === 0 && (
                    <Notify type="warning" {...styles.noQuotesText}>
                        {siteMessage("Rfq_NoQuotesMessage")}
                    </Notify>
                )}
                {quotesDataView.value.length > 0 && (
                    <GridItem {...styles.tableWrapper} width={12}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("َQuote #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Status")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("PO #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Requested")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Expires")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {quotesDataView.value.map(
                                    quote =>
                                        quote !== null && (
                                            <DataTableRow
                                                {...styles.dataTableRow}
                                                data-test-selector="recentQuotes_dataLine"
                                                key={quote.id}
                                            >
                                                <QuoteSummaryCard
                                                    quote={quote}
                                                    session={session}
                                                    quoteSettings={settingsCollection.quoteSettings}
                                                    extendedStyles={styles.quoteSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </SectionWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Recent Quotes",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
            {
                name: fields.viewLinkText,
                displayName: "View Link Text",
                editorTemplate: "TextField",
                defaultValue: "View All",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
